// CollectibleCard.jsx - Shared Pokemon-style EOS collectible card
// Used by: AircraftMarket.jsx (roll reveal), IntroSequence.jsx (intro screens)

import React from 'react';
import { getCollectibleImagePath } from '../data/aircraft.js';

// Rarity color scheme - matches AircraftMarket.jsx
const RARITY_COLORS = {
  mythic:    { primary: "#a855f7", text: "#d8b4fe", label: "MYTHIC", button: "CLAIM" },
  legendary: { primary: "#f59e0b", text: "#fcd34d", label: "LEGENDARY", button: "CLAIM" },
  anomalous: { primary: "#ef4444", text: "#fca5a5", label: "ANOMALOUS", button: "...WHAT?" },
  relic:     { primary: "#b8860b", text: "#daa520", label: "RELIC", button: "CLAIM" },
  rare:      { primary: "#60ffd0", text: "#a7f3d0", label: "RARE", button: "CLAIM" },
};

export function getRarityColors(rarity) {
  return RARITY_COLORS[rarity] || RARITY_COLORS.rare;
}

/**
 * CollectibleCard - The gorgeous Pokemon-style reveal card
 *
 * @param {Object} props
 * @param {Object} props.aircraft - The aircraft data object
 * @param {string} props.rarity - Rarity tier: rare, relic, anomalous, legendary, mythic
 * @param {boolean} props.isSilhouette - Show mystery silhouette instead of real image
 * @param {boolean} props.showHeader - Show "You also received..." header (default false)
 * @param {Function} props.onButtonClick - Optional button click handler
 * @param {string} props.className - Additional CSS classes
 */
export default function CollectibleCard({
  aircraft,
  rarity,
  isSilhouette = false,
  showHeader = false,
  onButtonClick,
  className = ''
}) {
  const colors = getRarityColors(rarity);

  return (
    <div className={`text-center py-4 ${className}`}>
      {/* Optional header */}
      {showHeader && (
        <div className="text-sm uppercase tracking-widest mb-2" style={{ color: "#525252" }}>
          Your GOLD A380 purchase triggered a bonus...
        </div>
      )}

      {/* Big reveal text */}
      <h1 className="text-4xl font-black mb-4 animate-pulse" style={{
        color: colors.primary,
        textShadow: `0 0 20px ${colors.primary}cc`
      }}>
        {colors.label}
      </h1>

      {/* Aircraft image with glow */}
      <div className="w-48 h-48 mx-auto mb-4 rounded-lg flex items-center justify-center relative overflow-hidden" style={{
        background: `radial-gradient(circle, ${colors.primary}4d 0%, ${colors.primary}1a 50%, transparent 70%)`,
        boxShadow: `0 0 40px ${colors.primary}60`
      }}>
        <div className="absolute inset-0 rounded-lg animate-ping opacity-10" style={{
          backgroundColor: colors.primary
        }} />

        {isSilhouette ? (
          <div
            className="text-8xl font-bold relative z-10 animate-pulse"
            style={{
              color: colors.primary,
              filter: 'blur(2px)',
              textShadow: `0 0 40px ${colors.primary}`,
            }}
          >
            ?
          </div>
        ) : (
          <>
            <img
              src={aircraft?.image || getCollectibleImagePath(aircraft?.aircraftId || aircraft?.model || aircraft?.id, rarity)}
              alt={aircraft?.name || 'Rare Aircraft'}
              className="w-full h-full object-contain p-4 relative z-10"
              style={{
                filter: `drop-shadow(0 0 15px ${colors.primary})`
              }}
              onError={(e) => {
                // Fallback to text if image fails
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="absolute inset-0 items-center justify-center hidden" style={{ display: 'none' }}>
              <span className="text-4xl font-bold" style={{
                color: colors.primary,
                filter: `drop-shadow(0 0 10px ${colors.primary})`
              }}>{colors.label}</span>
            </div>
          </>
        )}
      </div>

      {/* "You also received..." text - only when showing header */}
      {showHeader && !isSilhouette && (
        <div className="text-lg mb-1" style={{ color: "#737373" }}>You also received...</div>
      )}

      {/* Aircraft name - BIG */}
      <h2 className="text-3xl font-bold mb-3" style={{ color: colors.text }}>
        {isSilhouette ? '???' : aircraft?.name}
      </h2>

      {/* Description */}
      <p className="mb-4 text-sm italic" style={{ color: "#a3a3a3" }}>
        "{isSilhouette ? 'Details classified.' : aircraft?.description}"
      </p>

      {/* Tradeoffs warning for Anomalous */}
      {!isSilhouette && rarity === "anomalous" && aircraft?.tradeoffs?.length > 0 && (
        <div className="text-left p-4 rounded-lg mb-4" style={{
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          border: "1px solid #ef444440"
        }}>
          <div className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: "#ef4444" }}>
            WARNING - TRADEOFFS
          </div>
          <ul className="text-sm space-y-1">
            {aircraft.tradeoffs.map((tradeoff, i) => (
              <li key={i} style={{ color: "#fca5a5" }}>
                - {tradeoff.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Special abilities */}
      {!isSilhouette && aircraft?.specialAbilities && aircraft.specialAbilities.length > 0 && (
        <div className="text-left p-4 rounded-lg mb-4" style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          border: `1px solid ${colors.primary}40`
        }}>
          <div className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: colors.primary }}>
            {rarity === "anomalous" ? "Anomalous Properties" : "Special Abilities"}
          </div>
          <ul className="text-sm space-y-1">
            {aircraft.specialAbilities.map((ability, i) => (
              <li key={i} style={{ color: "#e5e5e5" }}>
                - {ability.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats bar */}
      {!isSilhouette && (
        <div className="grid grid-cols-3 gap-2 text-xs mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
          <div>
            <div style={{ color: "#525252" }}>Capacity</div>
            <div className="font-bold">{aircraft?.type === 'cargo' ? `${((aircraft?.cargoCapacity || 0) / 1000).toFixed(0)}k lbs` : `${aircraft?.capacity || 0} pax`}</div>
          </div>
          <div>
            <div style={{ color: "#525252" }}>Range</div>
            <div className="font-bold">{aircraft?.range?.toLocaleString() || "???"} nm</div>
          </div>
          <div>
            <div style={{ color: "#525252" }}>Speed</div>
            <div className="font-bold">{aircraft?.cruiseSpeed || "???"} kts</div>
          </div>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={onButtonClick}
        className="w-full text-lg py-4 font-bold rounded"
        style={{
          backgroundColor: colors.primary,
          color: "#000",
          boxShadow: `0 0 20px ${colors.primary}60`
        }}
      >
        {isSilhouette ? '???' : (aircraft?.claimButtonText || colors.button)}
      </button>

      {/* Tiny flavor text */}
      <div className="text-xs mt-3" style={{ color: "#333" }}>
        {isSilhouette
          ? 'One per server per month.'
          : rarity === "anomalous"
            ? "Good luck."
            : "Added to your fleet"}
      </div>
    </div>
  );
}
